import { cva } from 'class-variance-authority'

export const foundationButtonVariants = cva(
  'inline-flex items-center justify-center gap-2 whitespace-nowrap font-sans font-medium transition-[background-color,color,box-shadow,transform] duration-settle ease-precise focus-visible:outline-none disabled:pointer-events-none disabled:opacity-35 active:scale-98 active:duration-touch',
  {
    variants: {
      variant: {
        primary:
          'rounded-btn-md bg-primary-400 text-background-50 hover:bg-primary-500 focus-visible:shadow-[0_0_0_2px_#F4EFE2,0_0_0_4px_rgba(61,59,54,0.3)]',
        secondary:
          'rounded-btn-md bg-transparent text-ink-700 shadow-[inset_0_0_0_1px_rgba(61,59,54,0.2)] hover:bg-[rgba(61,59,54,0.04)] hover:shadow-[inset_0_0_0_1px_rgba(61,59,54,0.35)] focus-visible:shadow-[0_0_0_2px_#F4EFE2,0_0_0_4px_rgba(61,59,54,0.3)]',
        ghost:
          'rounded-btn-md bg-transparent text-ink-600 hover:bg-[rgba(61,59,54,0.05)] hover:text-ink-700 focus-visible:shadow-[0_0_0_2px_#F4EFE2,0_0_0_4px_rgba(61,59,54,0.3)]',
        destructive:
          'rounded-btn-md bg-transparent text-error-400 shadow-[inset_0_0_0_1px_rgba(181,112,106,0.25)] hover:bg-[rgba(181,112,106,0.06)] hover:text-error-500 hover:shadow-[inset_0_0_0_1px_rgba(181,112,106,0.4)] focus-visible:shadow-[0_0_0_2px_#F4EFE2,0_0_0_4px_rgba(181,112,106,0.3)]',
      },
      size: {
        sm: 'h-9 rounded-btn-sm px-4 text-[0.8125rem]',
        md: 'h-11 rounded-btn-md px-6 text-[0.8125rem]',
        lg: 'h-[52px] rounded-btn-lg px-8 text-fs-400',
        icon: 'h-11 w-11 rounded-btn-md p-0',
        'icon-sm': 'h-9 w-9 rounded-btn-sm p-0',
        'icon-lg': 'h-[52px] w-[52px] rounded-btn-lg p-0',
      },
      loading: {
        true: 'relative text-transparent transition-none hover:text-transparent cursor-wait',
        false: '',
      },
      focusIntensity: {
        subtle:
          'focus-visible:shadow-[0_0_0_2px_#F4EFE2,0_0_0_3px_rgba(61,59,54,0.25)]',
        normal: '',
        strong:
          'focus-visible:shadow-[0_0_0_2px_#F4EFE2,0_0_0_5px_rgba(61,59,54,0.32)]',
      },
    },
    defaultVariants: {
      variant: 'primary',
      size: 'md',
      loading: false,
      focusIntensity: 'normal',
    },
  }
)

export const badgeVariants = cva(
  'inline-flex items-center gap-1 rounded-md px-2 py-1 font-sans text-fs-200 font-medium uppercase tracking-[0.06em]',
  {
    variants: {
      status: {
        default: 'bg-[rgba(36,34,30,0.06)] text-ink-700',
        success: 'bg-success-100 text-success-500',
        warning: 'bg-warning-100 text-warning-500',
        error: 'bg-error-100 text-error-500',
        accent: 'bg-secondary-100 text-secondary-500',

        // Legacy aliases mapped to canonical surfaces.
        pending: 'bg-secondary-100 text-secondary-500',
        processing: 'bg-secondary-100 text-secondary-500',
        shipped: 'bg-[rgba(36,34,30,0.06)] text-ink-700',
        delivered: 'bg-success-100 text-success-500',
        cancelled: 'bg-error-100 text-error-500',
        unfulfilled: 'bg-warning-100 text-warning-500',
        printed: 'bg-success-100 text-success-500',
        fulfilled: 'bg-success-100 text-success-500',
        info: 'bg-secondary-100 text-secondary-500',
      },
      size: {
        sm: 'px-2 py-0.5',
        md: 'px-2.5 py-1',
        lg: 'px-3 py-1.5 text-fs-300',
      },
      style: {
        solid: '',
        outline: 'bg-transparent border border-[rgba(36,34,30,0.2)]',
        subtle: 'border-transparent opacity-90',
      },
    },
    defaultVariants: {
      status: 'default',
      size: 'md',
      style: 'solid',
    },
  }
)

export const cardVariants = cva(
  'rounded-card bg-white transition-[border-color,box-shadow,transform] duration-settle ease-precise',
  {
    variants: {
      variant: {
        default: 'border border-[rgba(36,34,30,0.06)]',
        shadow: 'shadow-sm',
        interactive:
          'cursor-pointer border border-[rgba(36,34,30,0.06)] hover:border-[rgba(36,34,30,0.12)] hover:shadow-md active:scale-[0.985] active:duration-touch',
        elevated: 'border-transparent shadow-lg',

        // Legacy aliases.
        outlined: 'border border-[rgba(36,34,30,0.2)]',
      },
      padding: {
        none: 'p-0',
        xs: 'p-space-xs',
        sm: 'p-space-sm',
        md: 'p-space-md',
        lg: 'p-space-lg',
        xl: 'p-space-xl',
        '2xl': 'p-space-4xl',
      },
      rounded: {
        sm: 'rounded-card-sm',
        md: 'rounded-card',
        lg: 'rounded-[20px]',
        full: 'rounded-full',
      },
    },
    defaultVariants: {
      variant: 'default',
      padding: 'md',
      rounded: 'md',
    },
  }
)

export const textVariants = cva('text-ink-700', {
  variants: {
    variant: {
      hero: 'font-serif text-fs-900 font-semibold tracking-tighter text-ink-700',
      display:
        'font-serif text-fs-800 font-semibold tracking-tight text-ink-700',
      title: 'font-serif text-fs-700 font-semibold tracking-tight text-ink-700',
      subtitle:
        'font-serif text-fs-600 font-semibold tracking-tight text-ink-700',
      body: 'font-sans text-fs-400 font-normal leading-relaxed text-ink-700',
      small: 'font-sans text-fs-300 font-normal tracking-wide text-ink-500',
      kicker:
        'font-sans text-fs-200 font-medium uppercase tracking-kicker text-ink-400',
      code: 'font-mono text-fs-300 font-normal text-ink-700',

      // Legacy aliases.
      h1: 'font-serif text-fs-700 font-semibold tracking-tight text-ink-700',
      h2: 'font-serif text-fs-600 font-semibold tracking-tight text-ink-700',
      h3: 'font-serif text-fs-600 font-semibold tracking-tight text-ink-700',
      bodyLanding:
        'font-sans text-fs-400 font-normal leading-relaxed text-ink-700',
      bodyFeature:
        'font-sans text-fs-400 font-normal leading-relaxed text-ink-700',
      caption: 'font-sans text-fs-300 font-normal tracking-wide text-ink-500',
      micro: 'font-sans text-fs-200 font-normal tracking-kicker text-ink-400',
    },
    size: {
      xs: 'text-fs-200',
      sm: 'text-fs-300',
      base: 'text-fs-400',
      lg: 'text-fs-600',
      xl: 'text-fs-700',
      '2xl': 'text-fs-800',
      '3xl': 'text-fs-900',
      '4xl': 'text-fs-900',
      '5xl': 'text-fs-900',
    },
    weight: {
      normal: 'font-normal',
      medium: 'font-medium',
      semibold: 'font-semibold',
    },
    textColor: {
      default: 'text-ink-700',
      muted: 'text-ink-600',
      subtle: 'text-ink-500',
      primary: 'text-primary-400',
      success: 'text-success-500',
      warning: 'text-warning-500',
      error: 'text-error-500',
      white: 'text-white',
    },
    align: {
      left: 'text-left',
      center: 'text-center',
      right: 'text-right',
      justify: 'text-justify',
    },
    truncate: {
      true: 'truncate',
      false: '',
    },
  },
  defaultVariants: {
    variant: 'body',
    align: 'left',
    truncate: false,
  },
})

export const boxVariants = cva('box-border', {
  variants: {
    display: {
      block: 'block',
      inline: 'inline',
      'inline-block': 'inline-block',
      flex: 'flex',
      'inline-flex': 'inline-flex',
      grid: 'grid',
      'inline-grid': 'inline-grid',
      none: 'hidden',
    },
    padding: {
      none: 'p-0',
      xs: 'p-1',
      sm: 'p-2',
      md: 'p-4',
      lg: 'p-6',
      xl: 'p-8',
      '2xl': 'p-12',
    },
    margin: {
      none: 'm-0',
      xs: 'm-1',
      sm: 'm-2',
      md: 'm-4',
      lg: 'm-6',
      xl: 'm-8',
      '2xl': 'm-12',
    },
    borderRadius: {
      none: 'rounded-none',
      sm: 'rounded-sm',
      base: 'rounded',
      md: 'rounded-md',
      lg: 'rounded-lg',
      xl: 'rounded-xl',
      '2xl': 'rounded-2xl',
      '3xl': 'rounded-3xl',
      full: 'rounded-full',
    },
    shadow: {
      none: 'shadow-none',
      sm: 'shadow-sm',
      base: 'shadow',
      md: 'shadow-md',
      lg: 'shadow-lg',
      xl: 'shadow-xl',
      soft: 'shadow-sm',
      glow: 'shadow-lg',
    },
    background: {
      transparent: 'bg-transparent',
      white: 'bg-white',
      canvas: 'bg-background-300',
      'neutral-50': 'bg-neutral-50',
      'neutral-100': 'bg-neutral-100',
      'neutral-200': 'bg-neutral-200',
      'primary-50': 'bg-background-50',
      'primary-100': 'bg-background-100',
      'primary-500': 'bg-primary-500',
      'primary-600': 'bg-primary-500',
    },
    border: {
      none: 'border-0',
      base: 'border border-neutral-200',
      primary: 'border border-primary-400',
      error: 'border border-error-400',
    },
  },
  defaultVariants: {
    display: 'block',
    padding: 'none',
    margin: 'none',
    borderRadius: 'none',
    shadow: 'none',
    background: 'transparent',
    border: 'none',
  },
})
